import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface ModalProps {
    isOpen: boolean
    onClose: () => void
    title?: string
    children: React.ReactNode
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
    className?: string
}

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
    className = ''
}) => {
    // Close modal on Escape key press
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscape)
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleEscape)
            document.body.style.overflow = 'unset'
        }
    }, [isOpen, onClose])

    if (!isOpen) return null

    const getSizeClasses = () => {
        switch (size) {
            case 'sm':
                return 'max-w-md'
            case 'md':
                return 'max-w-lg'
            case 'lg':
                return 'max-w-2xl'
            case 'xl':
                return 'max-w-4xl'
            case 'full':
                return 'max-w-full mx-4'
            default:
                return 'max-w-lg'
        }
    }

    const modalContent = (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                <div
                    className={`
                        card
                        relative transform overflow-hidden rounded-lg
                        text-left shadow-xl transition-all w-full
                        ${getSizeClasses()} ${className}
                    `}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    {title && (
                        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-neutral-200 dark:border-neutral-700">
                            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                                {title}
                            </h3>
                            <button
                                type="button"
                                onClick={onClose}
                                className="text-neutral-400 hover:text-neutral-500 dark:hover:text-neutral-300 transition-colors"
                            >
                                <span className="sr-only">Close</span>
                                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                    )}

                    {/* Content */}
                    <div className={title ? '' : 'relative'}>
                        {!title && (
                            <div className="absolute top-4 right-4 z-10">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="rounded-md bg-white dark:bg-neutral-800 text-neutral-400 hover:text-neutral-500 dark:hover:text-neutral-300 transition-colors"
                                >
                                    <span className="sr-only">Close</span>
                                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                        )}
                        {children}
                    </div>
                </div>
            </div>
        </div>
    )

    return createPortal(modalContent, document.body)
}

export default Modal